import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { CatalogsProvider, useCatalogs } from '../contexts/CatalogsProvider';
import IncidentClassification from '../components/incidents/IncidentClassification';
import SectionCards from '../components/incidents/SectionCards';
import FormRenderer from '../components/form/FormRenderer';
import ContactPortalShell from '../components/contact/ContactPortalShell';
import contactPortalService from '../services/contactPortal';
import { ToastContainer } from '../components/common/Toast';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../contexts/AuthContext';
import './Incidentes.scss';
import '../components/incidents/IncidentComponents.scss';
import './IframeTaskCreate.scss';

const initialData = {
  email: '',
  confirm_email: '',
  first_name: '',
  last_name: '',
  cuil: '',
  phone: '',
  phone2: '',
  address: '',
  contact_id: null,
  selectedContact: null,
  category_id: null,
  form_id: null,
  channel_id: 'web',
  category_name: '',
  form_name: '',
  fieldsSchema: null,
  loadingFields: false,
  errorFields: null,
  title: '',
  description: ''
};

const IframeTaskCreateContent = () => {
  const location = useLocation();
  const { user, loadingUser, setUser, fetchUser } = useAuth();
  const isIframeMode = useMemo(
    () => new URLSearchParams(location.search).get('iframe') === 'true',
    [location.search]
  );
  const showEmailStep = isIframeMode || !user;
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [needsContactData, setNeedsContactData] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completedSections, setCompletedSections] = useState({});
  const [sessionContactError, setSessionContactError] = useState('');
  const [search, setSearch] = useState('');

  const { catalogs, loading, getCategoriesByParent, getFormsByCategory, getFieldsSchema } = useCatalogs();
  const { toasts, success, error: showError, removeToast } = useToast();

  useEffect(() => {
    if (isIframeMode) {
      setCurrentStep(1);
      setSessionContactError('');
    }
  }, [isIframeMode]);

  const onLogout = async () => {
    try {
      await contactPortalService.logout();
    } finally {
      setUser(null);
      await fetchUser();
      window.location.href = '/contactos/login';
    }
  };

  useEffect(() => {
    if (isIframeMode) {
      setSessionContactError('');
      return;
    }

    if (loadingUser) {
      return;
    }

    if (!user) {
      setSessionContactError('');
      return;
    }

    if (!user.is_contact_portal) {
      if (!sessionContactError) {
        setSessionContactError('El usuario logueado no tiene un contacto asociado.');
        showError('El usuario logueado no tiene un contacto asociado.');
      }
      setCurrentStep((prev) => (prev < 2 ? 2 : prev));
      return;
    }

    setSessionContactError('');
    setNeedsContactData(false);
    setFormData((prev) => ({
      ...prev,
      email: user.email || prev.email,
      confirm_email: user.email || prev.confirm_email,
      first_name: user.first_name || prev.first_name,
      last_name: user.last_name || prev.last_name
    }));
    setCurrentStep((prev) => (prev < 2 ? 2 : prev));
  }, [isIframeMode, loadingUser, sessionContactError, showError, user]);

  const updateField = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const validateEmailStep = () => {
    const newErrors = {};
    const email = (formData.email || '').trim().toLowerCase();
    const confirmEmail = (formData.confirm_email || '').trim().toLowerCase();

    if (!email) {
      newErrors.email = 'Email es obligatorio';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email invalido';
    }

    if (!confirmEmail) {
      newErrors.confirm_email = 'Debe confirmar el email';
    } else if (confirmEmail !== email) {
      newErrors.confirm_email = 'Los emails no coinciden';
    }

    if (needsContactData) {
      if (!formData.first_name.trim()) {
        newErrors.first_name = 'Nombre es obligatorio';
      }
      const cuil = (formData.cuil || '').replace(/\D/g, '');
      if (!cuil) {
        newErrors.cuil = 'CUIL es obligatorio';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep = () => {
    if (currentStep === 1) {
      return validateEmailStep();
    }

    const newErrors = {};

    if (currentStep === 2) {
      if (!formData.category_id) {
        newErrors.category_id = 'Debe seleccionar una categoria';
      }
      if (!formData.form_id) {
        newErrors.form_id = 'Debe seleccionar un formulario';
      }
    }

    if (currentStep === 3 && formData.fieldsSchema?.sections) {
      const sortedSections = [...formData.fieldsSchema.sections].sort((a, b) => (a.order || 0) - (b.order || 0));
      sortedSections.forEach((section, idx) => {
        (section.fields || []).forEach((fieldDef) => {
          if (fieldDef.visible === false || !fieldDef.required) {
            return;
          }
          const key = `section_${idx}_field_${fieldDef.field.id}`;
          const val = formData[key];
          if (!val || val === '') {
            newErrors[key] = `${fieldDef.field.name} es requerido`;
          }
        });
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resolveEmailStep = async () => {
    if (!validateEmailStep()) {
      return;
    }

    const normalizedEmail = formData.email.trim().toLowerCase();

    if (!needsContactData) {
      try {
        setIsCheckingEmail(true);
        const check = await contactPortalService.checkEmail(normalizedEmail);

        if (check.exists && check.contact) {
          setFormData((prev) => ({
            ...prev,
            email: normalizedEmail,
            confirm_email: normalizedEmail,
            contact_id: check.contact.id,
            selectedContact: check.contact,
            first_name: check.contact.first_name || '',
            last_name: check.contact.last_name || '',
            cuil: check.contact.cuil || '',
            phone: check.contact.phone || '',
            phone2: check.contact.phone2 || '',
            address: check.contact.address || ''
          }));
          setNeedsContactData(false);
          setCurrentStep(2);
          return;
        }

        setNeedsContactData(true);
        showError('No existe contacto para ese email. Completa tus datos para continuar.');
      } catch (err) {
        showError(err.response?.data?.detail || 'No se pudo validar el email.');
      } finally {
        setIsCheckingEmail(false);
      }
      return;
    }

    setCurrentStep(2);
  };

  const nextStep = async () => {
    if (!isIframeMode && user && !user.is_contact_portal) {
      showError('No podes continuar: el usuario logueado no tiene contacto asociado.');
      return;
    }

    if (currentStep === 1) {
      if (!isIframeMode && user?.is_contact_portal) {
        setCurrentStep(2);
        return;
      }
      await resolveEmailStep();
      return;
    }

    if (!validateStep()) {
      return;
    }

    if (currentStep === 3) {
      setCurrentStep(5);
      return;
    }

    setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    if (currentStep === 5) {
      setCurrentStep(3);
      return;
    }

    if (!showEmailStep && currentStep <= 2) {
      return;
    }

    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const customFieldsPayload = useMemo(() => {
    const customFields = {};

    if (formData.fieldsSchema?.sections) {
      const sortedSections = [...formData.fieldsSchema.sections].sort((a, b) => (a.order || 0) - (b.order || 0));
      sortedSections.forEach((section, idx) => {
        (section.fields || []).forEach((fieldDef) => {
          const key = `section_${idx}_field_${fieldDef.field.id}`;
          const val = formData[key];
          if (val !== undefined && val !== null && val !== '') {
            customFields[fieldDef.field.id] = val;
          }
        });
      });
    }

    return customFields;
  }, [formData]);

  const sectionSummaries = useMemo(() => {
    if (!formData.fieldsSchema?.sections) {
      return [];
    }

    const sortedSections = [...formData.fieldsSchema.sections].sort((a, b) => (a.order || 0) - (b.order || 0));

    return sortedSections.map((section, sectionIndex) => {
      const fields = (section.fields || [])
        .filter((fieldDef) => fieldDef.visible !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((fieldDef) => {
          const key = `section_${sectionIndex}_field_${fieldDef.field.id}`;
          const rawValue = formData[key];
          const isEmpty = rawValue === undefined || rawValue === null || rawValue === '';

          if (isEmpty) {
            return null;
          }

          return {
            id: fieldDef.field.id,
            name: fieldDef.field.name,
            value: String(rawValue)
          };
        })
        .filter(Boolean);

      return {
        name: section.name,
        fields
      };
    }).filter((section) => section.fields.length > 0);
  }, [formData]);

  const handleSubmit = async () => {
    if (!isIframeMode && user && !user.is_contact_portal) {
      showError('No se puede crear la gestion porque el usuario logueado no tiene contacto asociado.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        email: formData.email.trim().toLowerCase(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        cuil: (formData.cuil || '').replace(/\D/g, ''),
        phone: formData.phone.trim(),
        phone2: formData.phone2.trim(),
        address: formData.address.trim(),
        category_id: formData.category_id,
        form_id: formData.form_id,
        channel_id: 'web',
        title: formData.title.trim(),
        description: formData.description.trim(),
        custom_fields: customFieldsPayload
      };

      const result = await contactPortalService.createIframeTask(payload);
      success(`Gestion creada exitosamente: ${result.id}`);

      setFormData(initialData);
      setErrors({});
      setNeedsContactData(false);
      setCompletedSections({});
      setCurrentStep(showEmailStep ? 1 : 2);
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        const newErrors = {};
        Object.entries(data).forEach(([key, value]) => {
          newErrors[key] = Array.isArray(value) ? value[0] : String(value);
        });
        setErrors(newErrors);
      }
      showError('No se pudo crear la gestion.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || loadingUser) {
    const loadingContent = (
      <div className={`incidentes-container iframe-task-create ${isIframeMode ? 'iframe-embed-mode' : ''}`}>
        <div className="container">
          <div className="loading">Cargando...</div>
        </div>
      </div>
    );

    if (isIframeMode) {
      return loadingContent;
    }

    return (
      <ContactPortalShell
        user={user}
        onLogout={onLogout}
        searchValue={search}
        onSearchChange={setSearch}
      >
        {loadingContent}
      </ContactPortalShell>
    );
  }

  if (!isIframeMode && !loadingUser && !user) {
    return <Navigate to="/contactos/login" replace />;
  }

  const pageContent = (
    <div className={`incidentes-container iframe-task-create ${isIframeMode ? 'iframe-embed-mode' : ''}`}>
      <div className="container">
        {!isIframeMode && (
          <div className="incidentes-header">
            <h1>Crear Consulta o Reclamo</h1>
            <p>Completa los pasos para registrar una nueva gestion.</p>
          </div>
        )}

        {!showEmailStep && sessionContactError && (
          <div className="iframe-inline-error error-message">{sessionContactError}</div>
        )}

        <div className="stepper">
          {showEmailStep && (
            <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
              <div className="step-number">1</div>
              <div className="step-label">Email</div>
            </div>
          )}
          <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
            <div className="step-number">{showEmailStep ? '2' : '1'}</div>
            <div className="step-label">Clasificacion</div>
          </div>
          <div className={`step ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
            <div className="step-number">{showEmailStep ? '3' : '2'}</div>
            <div className="step-label">Datos</div>
          </div>
          <div className={`step ${currentStep >= 5 ? 'active' : ''}`}>
            <div className="step-number">{showEmailStep ? '5' : '3'}</div>
            <div className="step-label">Confirmacion</div>
          </div>
        </div>

        <div className="form-container">
          {showEmailStep && currentStep === 1 && (
            <div className="form-step">
              <h3 className="step-title">Paso 1: Validacion de email</h3>

              {sessionContactError && (
                <div className="error-message">{sessionContactError}</div>
              )}

              <div className="form-row-2">
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="confirm_email">Confirmar email *</label>
                  <input
                    type="email"
                    id="confirm_email"
                    value={formData.confirm_email}
                    onChange={(e) => updateField('confirm_email', e.target.value)}
                    className={errors.confirm_email ? 'error' : ''}
                  />
                  {errors.confirm_email && <span className="error-message">{errors.confirm_email}</span>}
                </div>
              </div>

              {needsContactData && (
                <div className="iframe-contact-extra">
                  <div className="form-row-2">
                    <div className="form-group">
                      <label htmlFor="first_name">Nombre *</label>
                      <input
                        type="text"
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => updateField('first_name', e.target.value)}
                        className={errors.first_name ? 'error' : ''}
                      />
                      {errors.first_name && <span className="error-message">{errors.first_name}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="last_name">Apellido</label>
                      <input
                        type="text"
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => updateField('last_name', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="form-row-2">
                    <div className="form-group">
                      <label htmlFor="cuil">CUIL *</label>
                      <input
                        type="text"
                        id="cuil"
                        value={formData.cuil}
                        onChange={(e) => updateField('cuil', e.target.value.replace(/\D/g, ''))}
                        className={errors.cuil ? 'error' : ''}
                      />
                      {errors.cuil && <span className="error-message">{errors.cuil}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">Telefono</label>
                      <input
                        type="text"
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => updateField('phone', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="address">Direccion</label>
                    <input
                      type="text"
                      id="address"
                      value={formData.address}
                      onChange={(e) => updateField('address', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div className="form-step">
              <h3 className="step-title">Paso 2: Clasificacion</h3>
              <IncidentClassification
                catalogs={catalogs}
                incidentData={formData}
                onInputChange={updateField}
                getCategoriesByParent={getCategoriesByParent}
                getFormsByCategory={getFormsByCategory}
                getFieldsSchema={getFieldsSchema}
                errors={errors}
              />
            </div>
          )}

          {currentStep === 3 && (
            <div className="form-step">
              <h3 className="step-title">Paso 3: Datos especificos</h3>

              {formData.fieldsSchema?.initial_message && (
                <div
                  style={{
                    marginBottom: '1rem',
                    padding: '0.75rem 1rem',
                    border: '1px solid #d8dde6',
                    borderRadius: '8px',
                    backgroundColor: '#f8fafc'
                  }}
                  dangerouslySetInnerHTML={{ __html: formData.fieldsSchema.initial_message }}
                />
              )}

              <div className="form-group">
                <label htmlFor="title">Titulo</label>
                <input
                  id="title"
                  value={formData.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder="Describe brevemente el motivo"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Descripcion</label>
                <textarea
                  id="description"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Detalle adicional"
                />
              </div>

              {formData.fieldsSchema?.sections ? (
                <SectionCards
                  sections={formData.fieldsSchema.sections}
                  values={formData}
                  onChange={updateField}
                  errors={errors}
                  completedSections={completedSections}
                  onSectionComplete={(sectionIndex, isCompleted) =>
                    setCompletedSections((prev) => ({ ...prev, [sectionIndex]: isCompleted }))
                  }
                />
              ) : (
                <FormRenderer
                  schema={formData.fieldsSchema}
                  values={formData}
                  onChange={updateField}
                  errors={errors}
                />
              )}
            </div>
          )}

          {currentStep === 5 && (
            <div className="form-step">
              <h3 className="step-title">Paso 5: Confirmacion</h3>

              <div className="iframe-confirm-grid">
                <div className="summary-item"><span className="label">Email:</span><span>{formData.email}</span></div>
                <div className="summary-item"><span className="label">Nombre:</span><span>{`${formData.first_name || ''} ${formData.last_name || ''}`.trim() || '-'}</span></div>
                <div className="summary-item"><span className="label">CUIL:</span><span>{formData.cuil || '-'}</span></div>
                <div className="summary-item"><span className="label">Categoria:</span><span>{formData.category_name || '-'}</span></div>
                <div className="summary-item"><span className="label">Formulario:</span><span>{formData.form_name || '-'}</span></div>
                <div className="summary-item"><span className="label">Titulo:</span><span>{formData.title || '-'}</span></div>
              </div>

              {sectionSummaries.length > 0 && (
                <div className="iframe-sections-summary">
                  <h4>Formularios y secciones</h4>

                  {sectionSummaries.map((section) => (
                    <div key={section.name} className="iframe-section-card">
                      <div className="iframe-section-title">{section.name}</div>

                      <div className="iframe-section-fields">
                        {section.fields.map((field) => (
                          <div key={field.id} className="iframe-section-field">
                            <span className="field-label">{field.name}</span>
                            <span className="field-value">{field.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {sectionSummaries.length === 0 && (
                <div className="iframe-empty-sections">No hay campos adicionales completados.</div>
              )}
            </div>
          )}

          <div className="form-actions">
            {currentStep > (showEmailStep ? 1 : 2) && (
              <button type="button" className="btn btn-secondary" onClick={prevStep} disabled={isSubmitting || isCheckingEmail}>
                Anterior
              </button>
            )}

            {currentStep < 5 && (
              <button type="button" className="btn btn-primary" onClick={nextStep} disabled={isSubmitting || isCheckingEmail}>
                {isCheckingEmail ? 'Validando...' : 'Siguiente'}
              </button>
            )}

            {currentStep === 5 && (
              <button type="button" className="btn btn-success" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Enviando...' : 'Confirmar y enviar'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (isIframeMode) {
    return (
      <>
        {pageContent}
        <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
      </>
    );
  }

  return (
    <ContactPortalShell
      user={user}
      onLogout={onLogout}
      searchValue={search}
      onSearchChange={setSearch}
    >
      {pageContent}

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </ContactPortalShell>
  );
};

const IframeTaskCreate = () => (
  <CatalogsProvider>
    <IframeTaskCreateContent />
  </CatalogsProvider>
);

export default IframeTaskCreate;
